import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, X } from "lucide-react";
import ProtocolEditorPanel, { validateProtocol, normalizeProtocolForForm } from "@/components/ProtocolEditorPanel";

/** Per-patient protocol snapshot editor.
 * Loads the snapshot from a specific `active_protocols[]` entry (via ?ap=<ap_id>)
 * or falls back to the singleton `active_protocol` for legacy links.
 * The master template is NEVER touched. Changes reflect on the patient's
 * dashboard within one polling cycle (~20s). */
export default function AdminPatientProtocolEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apId = searchParams.get("ap") || null;
  const [patient, setPatient] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/patients/${id}`);
      setPatient(data.patient);
      let snapshot = null;
      if (apId) {
        // Fetch the specific active_protocols[] entry (returns full snapshot)
        try {
          const { data: entry } = await api.get(`/admin/patients/${id}/active-protocols/${apId}`);
          snapshot = entry.snapshot;
        } catch (_) {
          toast.error("Protocolo do paciente não encontrado.");
          return;
        }
      } else {
        // Legacy: singleton
        snapshot = data.protocol;
      }
      if (!snapshot) {
        toast.error("Este paciente não tem protocolo iniciado.");
        return;
      }
      setEditing(normalizeProtocolForForm(snapshot));
    } catch (_) { toast.error("Erro ao carregar paciente."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, apId]);

  const save = async () => {
    const err = validateProtocol(editing);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        category: editing.category,
        duration_days: Number(editing.duration_days),
        description: editing.description,
        indications: editing.indications || [],
        phases: editing.phases.map((ph) => ({
          id: ph.id, name: ph.name,
          day_start: Number(ph.day_start), day_end: Number(ph.day_end),
          objectives: ph.objectives || [], supplementation: ph.supplementation || [],
          expected_symptoms: ph.expected_symptoms || "",
        })),
        checkin_questions: editing.checkin_questions || [],
        checklist_items: editing.checklist_items || [],
        symptom_items: editing.symptom_items || [],
        education: editing.education || { html: "", links: [], pdfs: [], items: [] },
      };
      const url = apId
        ? `/admin/patients/${id}/protocol-snapshot?ap_id=${encodeURIComponent(apId)}`
        : `/admin/patients/${id}/protocol-snapshot`;
      await api.patch(url, payload);
      toast.success("Protocolo do paciente atualizado.");
      navigate(`/admin/pacientes/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  if (!editing) return <div className="text-sm text-muted-foreground overline">Carregando…</div>;

  return (
    <div className="space-y-6" data-testid="admin-patient-protocol-edit">
      <div>
        <Link to={`/admin/pacientes/${id}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-patient">
          <ArrowLeft className="w-4 h-4" /> Voltar ao paciente
        </Link>
        <div className="mt-3">
          <div className="overline text-gold mb-1">Editar protocolo do paciente</div>
          <h1 className="brand-wordmark uppercase text-3xl md:text-4xl font-bold tracking-tight text-primary">{patient?.name || "…"}</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
            Alterações aqui aplicam-se <b>somente a este paciente</b>. O template original não é afetado. O dashboard do paciente atualiza em até 20 segundos.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm flex flex-col max-h-[calc(100vh-260px)] min-h-[500px]">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="overline text-muted-foreground mb-1">Snapshot do protocolo</div>
            <h3 className="font-heading font-semibold text-lg">{editing.name || "Sem nome"}</h3>
          </div>
          <Link to={`/admin/pacientes/${id}`} className="p-1 hover:bg-muted rounded-sm">
            <X className="w-4 h-4" />
          </Link>
        </div>

        <ProtocolEditorPanel value={editing} onChange={setEditing} />

        <div className="border-t border-border p-4 flex gap-2 justify-end">
          <Link to={`/admin/pacientes/${id}`} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-patient-protocol-btn">Cancelar</Link>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
            data-testid="save-patient-protocol-btn">
            <Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}
